import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { History } from '../../../../_models/History';
import { CdkDragEnd, CdkDragMove } from '@angular/cdk/drag-drop';
import { MatIcon } from '@angular/material/icon';
import { EsriHelpers } from '../../../../_helpers/EsriHelpers';
import { MessageService } from '../../../../_services/local/message.service';

@Component({
  selector: 'app-historique-slider',
  templateUrl: './historique-slider.component.html',
  styleUrls: ['./historique-slider.component.scss'],
})
export class HistoriqueSliderComponent implements OnInit, AfterViewInit {
  @Input() form: History | undefined;
  @Input() whereClauseFields!: {
    fieldDateStart: string;
    fieldDateEnd: string;
  };
  @Input() loading!: boolean;
  @Output() showForm = new EventEmitter();
  @Output() select = new EventEmitter<{ label: string; whereClause: string }>();
  @Output() label = new EventEmitter<string>();
  @ViewChild('items') items!: ElementRef<HTMLUListElement>;
  @ViewChild('handle') handle!: MatIcon;

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (event.key === 'ArrowLeft') {
      this.prev();
    } else if (event.key === 'ArrowRight') {
      this.next();
    }
  }

  dragHandlePosition: any;
  selectionWidth: string = '0';
  ticks: Array<{ label: string; whereClause: string }> = [];
  tickElements!: NodeListOf<HTMLLIElement>;
  currentIndex = 0;

  private readonly selectorsSize = 10 + 80 + 7 + 40 + 7;
  private readonly dragHandleX = -7;
  private readonly dragHandleY = -4;
  private readonly maxNumberOfTickWhereDisplayIsFine = 80;
  private numberOfTicks = 0;
  private dropZones: number[] = [0];

  constructor(private messageService: MessageService) {
    this.dragHandlePosition = { x: this.dragHandleX, y: this.dragHandleY };
  }

  ngOnInit(): void {
    this.initializeTicks();
  }

  ngAfterViewInit() {
    this.initializeTickElements();
    setTimeout(() => {
      this.emitSelection(0);
    });
  }

  selectItem(item: { label: string; whereClause: string }, i: number) {
    this.moveHandleTo(i);
    this.emitSelection(i);
  }

  prev() {
    if (this.currentIndex >= 1) {
      this.currentIndex--;
      this.moveHandleTo(this.currentIndex);
      console.debug('prev', this.ticks[this.currentIndex].label);
      this.emitSelection(this.currentIndex);
    }
  }

  next() {
    if (this.currentIndex < this.ticks.length - 1) {
      this.currentIndex++;
      this.moveHandleTo(this.currentIndex);
      console.debug('next', this.ticks[this.currentIndex].label);
      this.emitSelection(this.currentIndex);
    }
  }

  dragMoved(event: CdkDragMove) {
    const parentBbox = this.items.nativeElement.getBoundingClientRect();

    this.selectionWidth = event.pointerPosition.x - parentBbox.x + 'px';

    const x = event.pointerPosition.x - parentBbox.x;
    for (let i = 0; i < this.dropZones.length - 1; i++) {
      if (x > this.dropZones[i] && x <= this.dropZones[i + 1]) {
        this.emitSelection(i + 1);
      }
    }
  }

  dragEnded(event: CdkDragEnd) {
    const parentBbox = this.items.nativeElement.getBoundingClientRect();

    const x = event.dropPoint.x - parentBbox.x;
    const dropZonesLastIndex = this.dropZones.length - 1;

    if (x <= 0) {
      this.moveHandleTo(0);
      this.emitSelection(0);
      return;
    } else if (x >= this.dropZones[dropZonesLastIndex]) {
      this.moveHandleTo(dropZonesLastIndex);
      this.emitSelection(dropZonesLastIndex);
      return;
    }

    for (let i = 0; i < dropZonesLastIndex; i++) {
      if (x > this.dropZones[i] && x <= this.dropZones[i + 1]) {
        this.moveHandleTo(i + 1);
        this.emitSelection(i + 1);
      }
    }
  }

  private moveHandleTo(index: number) {
    this.currentIndex = index;
    this.dragHandlePosition = {
      x: this.dropZones[this.currentIndex] + this.dragHandleX,
      y: this.dragHandleY,
    };
    this.selectionWidth = this.dropZones[this.currentIndex] + 'px';
  }

  private initializeDropZones() {
    setTimeout(() => {
      this.tickElements.forEach((el, index) => {
        if (index > 0) {
          this.dropZones.push(el.offsetLeft);
        }
      });
    });
  }

  private initializeTickElements() {
    setTimeout(() => {
      this.tickElements = this.items.nativeElement.querySelectorAll<HTMLLIElement>('li.tick');
      if (this.tickElements && this.tickElements.length) {
        const width = Math.ceil(this.items.nativeElement.clientWidth / this.tickElements.length) - 2;
        this.tickElements.forEach((el) => (el.style.width = width + 'px'));
        this.initializeDropZones();
      }
    });
  }

  private initializeTicks() {
    if (!this.form) {
      return;
    }

    this.generateTicks(this.form);
    console.debug(this.ticks);
    this.numberOfTicks = this.ticks.length;
  }

  private generateTicks(history: History) {
    const hoursMinsStart = history.recurrenceHourStart.split(':').map((x) => Number(x));

    const numberOfDays = HistoriqueSliderComponent.getDays(history.consultation.start, history.consultation.end);
    const minuteStart = history.consultation.start;
    const minuteEnd = history.consultation.end;

    const numberOfMinutes = HistoriqueSliderComponent.getMinutes(minuteStart, minuteEnd);
    console.debug('days, minutes', numberOfDays, numberOfMinutes);

    if (numberOfMinutes / history.step > this.maxNumberOfTickWhereDisplayIsFine) {
      this.messageService.info(
        `L'affichage de l'outil d'historisation est optimisé pour un nombre de tick inférieur à ${this.maxNumberOfTickWhereDisplayIsFine} `
      );
    }

    const isEveryDay = history.recurrence === 7;

    const hoursMinsEnd = history.recurrenceHourEnd.split(':').map((x) => Number(x));
    const current = new Date(history.consultation.start);
    current.setDate(current.getDate() + (isEveryDay ? 0 : (history.recurrence - current.getDay() + 7) % 7));
    current.setHours(hoursMinsStart[0], hoursMinsStart[1], 0, 0);
    history.consultation.end.setHours(hoursMinsEnd[0], hoursMinsEnd[1], 0, 0);

    while (current <= history.consultation.end) {
      for (let minute = 0; minute < numberOfMinutes; minute = minute + history.step) {
        this.ticks.push({
          label: EsriHelpers.formatDate(current, true),
          whereClause: history.formatWhereClause(history.consultation.start, current),
        });
        current.setMinutes(current.getMinutes() + history.step, 0, 0);
      }
      current.setDate(current.getDate() + (isEveryDay ? 1 : 7));
      current.setHours(hoursMinsStart[0], hoursMinsStart[1], 0, 0);
    }
  }

  private emitLabel(index: number) {
    if (index >= 0 && index < this.numberOfTicks) {
      this.label.emit(this.ticks[index].label);
    }
  }

  private emitSelection(index: number) {
    if (index >= 0 && index < this.numberOfTicks) {
      this.select.emit(this.ticks[index]);
    }
  }

  private static getDays(start: Date, end: Date): number {
    const difference = end.getTime() - start.getTime();
    return Math.ceil(difference / (1000 * 3600 * 24)) + 1;
  }

  private static getMinutes(start: Date, end: Date): number {
    const difference = end.getTime() - start.getTime();
    return Math.ceil(difference / (1000 * 60)) + 1;
  }
}
